import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePortfolioAuth } from '@/context/PortfolioAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditProperty() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, isAuthenticated, logout } = usePortfolioAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingProperty, setFetchingProperty] = useState(true);
  const [propertyData, setPropertyData] = useState({
    address: '',
    purchase_price: '',
    purchase_date: '',
    down_payment: '',
    mortgage_principal: '',
    interest_rate: '',
    mortgage_term: '',
    monthly_rent: '',
    property_type: 'Single Family',
    bedrooms: '',
    bathrooms: '',
    square_feet: ''
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/portfolio/login');
      return;
    }
    fetchProperty();
  }, [id, isAuthenticated, navigate]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/portfolio/properties/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const property = response.data;
      setPropertyData({
        address: property.address || '',
        purchase_price: property.purchase_price?.toString() || '',
        purchase_date: property.purchase_date || '',
        down_payment: property.down_payment?.toString() || '',
        mortgage_principal: property.mortgage_principal?.toString() || '',
        interest_rate: property.interest_rate?.toString() || '',
        mortgage_term: property.mortgage_term?.toString() || '',
        monthly_rent: property.monthly_rent?.toString() || '',
        property_type: property.property_type || 'Single Family',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        square_feet: property.square_feet?.toString() || ''
      });
    } catch (error) {
      console.error('Error fetching property:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/portfolio/login');
      } else if (error.response?.status === 404) {
        toast.error('Property not found');
        navigate('/portfolio');
      } else {
        toast.error('Failed to load property details');
      }
    } finally {
      setFetchingProperty(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert string values to numbers
      const payload = {
        ...propertyData,
        purchase_price: parseFloat(propertyData.purchase_price),
        down_payment: parseFloat(propertyData.down_payment),
        mortgage_principal: parseFloat(propertyData.mortgage_principal),
        interest_rate: parseFloat(propertyData.interest_rate),
        mortgage_term: parseInt(propertyData.mortgage_term),
        monthly_rent: parseFloat(propertyData.monthly_rent),
        bedrooms: parseInt(propertyData.bedrooms) || 0,
        bathrooms: parseFloat(propertyData.bathrooms) || 0,
        square_feet: parseInt(propertyData.square_feet) || 0
      };

      await axios.put(`${API}/portfolio/properties/${id}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success('Property updated successfully!');
      navigate('/portfolio');
      
    } catch (error) {
      console.error('Error updating property:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/portfolio/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update property');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingProperty) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1e3a8a] mx-auto" />
          <p className="mt-4 text-slate-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/portfolio')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Portfolio
          </Button>
          <h1 className="text-3xl font-bold text-[#1e3a8a]">Edit Property</h1>
          <p className="text-slate-600 mt-2">Update your property details</p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-[#1e3a8a]">Property Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Property Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Property Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Property Address *</Label>
                    <Input
                      id="address"
                      value={propertyData.address}
                      onChange={(e) => setPropertyData({ ...propertyData, address: e.target.value })}
                      placeholder="123 Main St, City, State ZIP"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="property_type">Property Type</Label>
                    <select
                      id="property_type"
                      value={propertyData.property_type}
                      onChange={(e) => setPropertyData({ ...propertyData, property_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]"
                    >
                      <option>Single Family</option>
                      <option>Multi Family</option>
                      <option>Condo</option>
                      <option>Townhouse</option>
                      <option>Commercial</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="purchase_date">Purchase Date *</Label>
                    <Input
                      id="purchase_date"
                      type="date"
                      value={propertyData.purchase_date}
                      onChange={(e) => setPropertyData({ ...propertyData, purchase_date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      value={propertyData.bedrooms}
                      onChange={(e) => setPropertyData({ ...propertyData, bedrooms: e.target.value })}
                      placeholder="3"
                      min="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      step="0.5"
                      value={propertyData.bathrooms}
                      onChange={(e) => setPropertyData({ ...propertyData, bathrooms: e.target.value })}
                      placeholder="2.5"
                      min="0"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="square_feet">Square Feet</Label>
                    <Input
                      id="square_feet"
                      type="number"
                      value={propertyData.square_feet}
                      onChange={(e) => setPropertyData({ ...propertyData, square_feet: e.target.value })}
                      placeholder="2000"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Financial Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Financial Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="purchase_price">Purchase Price * ($)</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      value={propertyData.purchase_price}
                      onChange={(e) => setPropertyData({ ...propertyData, purchase_price: e.target.value })}
                      placeholder="350000"
                      required
                      min="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="down_payment">Down Payment * ($)</Label>
                    <Input
                      id="down_payment"
                      type="number"
                      step="0.01"
                      value={propertyData.down_payment}
                      onChange={(e) => setPropertyData({ ...propertyData, down_payment: e.target.value })}
                      placeholder="70000"
                      required
                      min="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="mortgage_principal">Mortgage Principal * ($)</Label>
                    <Input
                      id="mortgage_principal"
                      type="number"
                      step="0.01"
                      value={propertyData.mortgage_principal}
                      onChange={(e) => setPropertyData({ ...propertyData, mortgage_principal: e.target.value })}
                      placeholder="280000"
                      required
                      min="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="interest_rate">Interest Rate * (%)</Label>
                    <Input
                      id="interest_rate"
                      type="number"
                      step="0.01"
                      value={propertyData.interest_rate}
                      onChange={(e) => setPropertyData({ ...propertyData, interest_rate: e.target.value })}
                      placeholder="6.5"
                      required
                      min="0"
                      max="100"
                    />
                  </div>

                  <div>
                    <Label htmlFor="mortgage_term">Mortgage Term * (months)</Label>
                    <Input
                      id="mortgage_term"
                      type="number"
                      value={propertyData.mortgage_term}
                      onChange={(e) => setPropertyData({ ...propertyData, mortgage_term: e.target.value })}
                      placeholder="360"
                      required
                      min="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">360 months = 30 years</p>
                  </div>

                  <div>
                    <Label htmlFor="monthly_rent">Monthly Rent * ($)</Label>
                    <Input
                      id="monthly_rent"
                      type="number"
                      step="0.01"
                      value={propertyData.monthly_rent}
                      onChange={(e) => setPropertyData({ ...propertyData, monthly_rent: e.target.value })}
                      placeholder="2500"
                      required
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button
                  type="button"
                  onClick={() => navigate('/portfolio')}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
